import assertNever from 'assert-never';

export enum ScheduledJobType {
	FirmwareUpdate = 'FIRMWARE_UPDATE'
}

export enum ScheduledJobStatus {
	Pending = 'PENDING',
	Executing = 'EXECUTING',
	Completed = 'COMPLETED',
	Failed = 'FAILED',
	Cancelled = 'CANCELLED',
	Missed = 'MISSED'
}

export interface ScheduledJob {
	id: string;
	jobId: string;
	name: string;
	type: ScheduledJobType;
	status: ScheduledJobStatus;
	requestCount: number;
	executeAt: Date;
	createdBy: string;
	createdAt: Date;
	updatedBy: string;
	updatedAt: Date;
}

export interface CreateScheduledJobData {
	deviceIds: string[];
	jobType: ScheduledJobType;
}
export interface ScheduledJobRequest {
	id: string;
	type: ScheduledJobType;
	status: ScheduledJobStatus;
	jobId: string;
	executeAt: string;
}

export interface FirmwareUpdateJobRequest extends ScheduledJobRequest {
	type: ScheduledJobType.FirmwareUpdate;
	packageKey: string;
	packageVersion: string;
	hosted: boolean;
	deviceHardwareId: string;
}

export interface UpcomingScheduledJobs {
	firmwareUpdate?: ScheduledJobRequest;
}

export function getUpcomingScheduledJobs(
	devices: { features: { scheduledJobs?: { scheduledJobRequests: ScheduledJobRequest[] } } }[]
): UpcomingScheduledJobs {
	if (devices.length === 0) {
		return {};
	}

	const device1PendingJobRequests = (devices[0].features.scheduledJobs?.scheduledJobRequests ?? []).filter(
		(jobRequest) => jobRequest.status === ScheduledJobStatus.Pending
	);
	const firmwareUpdateJobs: ScheduledJobRequest[] = [];

	if (devices.length === 1) {
		// If single device selected, partition its pending jobs by type
		for (const jobRequest of device1PendingJobRequests) {
			switch (jobRequest.type) {
				case ScheduledJobType.FirmwareUpdate:
					firmwareUpdateJobs.push(jobRequest);
					break;
				default:
					assertNever(jobRequest.type);
			}
		}
	} else if (devices.length > 1) {
		// If multiple devices selected, find pending jobs that all devices are part of and partition by type
		for (const device1JobRequest of device1PendingJobRequests) {
			const areAllDevicesInJob = devices
				.slice(1)
				.every(
					(device) =>
						device.features.scheduledJobs?.scheduledJobRequests.some(
							(jobRequest) => jobRequest.jobId === device1JobRequest.jobId
						)
				);
			if (areAllDevicesInJob) {
				switch (device1JobRequest.type) {
					case ScheduledJobType.FirmwareUpdate:
						firmwareUpdateJobs.push(device1JobRequest);
						break;
					default:
						assertNever(device1JobRequest.type);
				}
			}
		}
	}

	const sortedFirmwareUpdateJobs = firmwareUpdateJobs.sort((a, b) => {
		const dateATimestamp = new Date(a.executeAt).getTime();
		const dateBTimestamp = new Date(b.executeAt).getTime();
		return dateATimestamp - dateBTimestamp;
	});

	return {
		firmwareUpdate: sortedFirmwareUpdateJobs.length > 0 ? sortedFirmwareUpdateJobs[0] : undefined
	};
}
